import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getGeoRegionalState, getGeoWoreda } from "../../services/apiServices.js";

// Factor colors
const factorColors = {
  temperature: {
    "<10°C": "#99ccff",
    "10-15°C": "#66b2ff",
    "15-20°C": "#3399ff",
    "20-25°C": "#0073e6",
    "25-30°C": "#0052b3",
    "30-35°C": "#003366",
    "No data": "#99b3cc",
  },
  humidity: {
    "<25%": "#99ccff",
    "25-35%": "#66b2ff",
    "35-50%": "#3399ff",
    "50-65%": "#0073e6",
    "65-80%": "#0052b3",
    "No data": "#99b3cc",
  },
  rainfall: {
    "<100mm": "#99ccff",
    "100-300mm": "#66b2ff",
    "300-600mm": "#3399ff",
    "600-900mm": "#0073e6",
    "900-1100mm": "#0052b3",
    "No data": "#99b3cc",
  },
};

// Get category from factor
const getCategory = (factor, value) => {
  if (value == null) return "No data";
  switch (factor) {
    case "temperature":
      if (value < 10) return "<10°C";
      if (value < 15) return "10-15°C";
      if (value < 20) return "15-20°C";
      if (value < 25) return "20-25°C";
      if (value < 30) return "25-30°C";
      return "30-35°C";
    case "humidity":
      if (value < 25) return "<25%";
      if (value < 35) return "25-35%";
      if (value < 50) return "35-50%";
      if (value < 65) return "50-65%";
      return "65-80%";
    case "rainfall":
      if (value < 100) return "<100mm";
      if (value < 300) return "100-300mm";
      if (value < 600) return "300-600mm";
      if (value < 900) return "600-900mm";
      return "900-1100mm";
    default:
      return "No data";
  }
};

// Styles
const woredaStyle = { color: "#009933", weight: 1, fillOpacity: 0.6 };
const regionStyle = { color: "#003366", weight: 1.5, fillOpacity: 0.1 };
const highlightStyle = { color: "#ff6600", weight: 2, fillOpacity: 0.6 };

export default function WeatherMap() {
  const [geojsonData, setGeojsonData] = useState(null);
  const [regions, setRegions] = useState(null);
  const [selectedFactor, setSelectedFactor] = useState("temperature");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const mapRef = useRef();

  // Fetch data
  useEffect(() => {
    async function fetchGeoData() {
      try {
        const regionalData = await getGeoRegionalState();
        setRegions({
          type: "FeatureCollection",
          features: regionalData.map((r) => ({
            type: "Feature",
            geometry: r.geometry,
            properties: { REGIONNAME: r.regionalState },
          })),
        });
        getGeoWoreda(100, (batchData) => {
          setGeojsonData((prevData) => {
            return prevData
              ? { type: "FeatureCollection", features: [...prevData.features, ...batchData] }
              : { type: "FeatureCollection", features: batchData };
          });
        });
      } catch (err) {
        console.error("Failed to load geo data:", err);
      }
    }
    fetchGeoData();
  }, []);

  // Render region layer
  const RegionLayer = ({ data }) => {
    const map = useMap();

    const onEachFeature = (feature, layer) => {
      layer.setStyle(regionStyle);

      layer.on({
        mouseover: () =>
          layer.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0.3 }),
        mouseout: () => layer.setStyle(regionStyle),
      });

      // Permanent tooltip for region
      layer.bindTooltip(feature.properties.REGIONNAME, {
        permanent: true,
        direction: "center",
        className: "region-tooltip",
      });

      // Optional popup on click
      layer.bindPopup(`<b>${feature.properties.REGIONNAME}</b>`);
    };

    return <GeoJSON data={data} onEachFeature={onEachFeature} pane="regionPane" />;
  };

  // Render woreda layer with optimization
  const WoredaLayer = ({ data, selectedFactor, selectedCategories }) => {
    const layerRef = useRef();

    const getFillStyle = useCallback((feature) => {
      const value =
        selectedFactor === "temperature"
          ? feature.properties?.factors?.temperature_C
          : selectedFactor === "humidity"
            ? feature.properties?.factors?.humidity
            : feature.properties?.factors?.rainfall;

      const category = getCategory(selectedFactor, value);

      // Only show selected categories; hide others
      if (selectedCategories.length > 0 && !selectedCategories.includes(category)) {
        return { ...woredaStyle, fillOpacity: 0, opacity: 0 }; // invisible
      }

      return {
        ...woredaStyle,
        fillColor: factorColors[selectedFactor][category] || "#cccccc",
        fillOpacity: 0.6,
        opacity: 1,
      };
    }, [selectedFactor, selectedCategories]);

    const onEachFeature = (feature, layer) => {
      layer.setStyle(getFillStyle(feature));

      layer.on({
        mouseover: () => layer.setStyle({ ...highlightStyle, fillOpacity: 0.6 }),
        mouseout: () => layer.setStyle(getFillStyle(feature)),
      });

      layer.bindPopup(
        `<b>${feature.properties.WOREDANAME}</b><br/>
       Region: ${feature.properties.REGIONNAME}<br/>
       Zone: ${feature.properties.ZONENAME}<br/>
       ${selectedFactor.toUpperCase()}: ${feature.properties.factors?.[selectedFactor + (selectedFactor === 'temperature' ? '_C' : '')] ?? "No data"
        }`
      );
    };

    useEffect(() => {
      if (layerRef.current) {
        layerRef.current.eachLayer((layer) => {
          layer.setStyle(getFillStyle(layer.feature));
        });
      }
    }, [selectedCategories, selectedFactor, getFillStyle]);

    return <GeoJSON data={data} onEachFeature={onEachFeature} ref={layerRef} pane="woredaPane" />;
  };

  // LEGEND
  const Legend = React.memo(({ selectedFactor, selectedCategories, setSelectedCategories }) => {
    const map = useMap();

    useEffect(() => {
      const legend = L.control({ position: "bottomright" });

      legend.onAdd = () => {
        const div = L.DomUtil.create("div", "info legend");
        div.style.background = "#fff";
        div.style.padding = "16px";
        div.style.borderRadius = "5px";
        div.style.fontFamily = "Poppins, sans-serif";
        div.style.fontSize = "14px";
        div.innerHTML = `<p style="font-weight:700; margin-top:0;">${selectedFactor.toUpperCase()} (2025)</p>`;
        if (selectedCategories.length > 0) {
          const clearFilter = document.createElement("div");
          clearFilter.style.cursor = "pointer";
          clearFilter.style.marginBottom = "8px";
          clearFilter.style.color = "#024795";
          clearFilter.style.fontWeight = "600";
          clearFilter.innerText = "Clear Filter";
          clearFilter.onclick = () => setSelectedCategories([]);
          div.appendChild(clearFilter);
        }
        for (const [category, color] of Object.entries(factorColors[selectedFactor])) {
          const item = document.createElement("div");
          item.style.cursor = "pointer";
          item.style.marginBottom = "4px";
          item.style.textDecoration = selectedCategories.includes(category) ? "underline" : "none";

          item.innerHTML =
            `<i style="background:${color}; width:18px; height:18px; float:left; margin-right:8px; opacity:0.7; border-radius:3px;"></i>` +
            category;
          item.onclick = () => {
            // Toggle category selection
            setSelectedCategories((prev) =>
              prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
            );
          };
          div.appendChild(item);
        }
        return div;
      };

      legend.addTo(map);
      return () => legend.remove();
    }, [map, selectedFactor, selectedCategories, setSelectedCategories]);

    return null;
  });

  useEffect(() => {
    setSelectedCategories([]);
  }, [selectedFactor]);

  // Map Layers component: create panes before rendering layers
  const MapLayers = () => {
    const map = useMap();

    useEffect(() => {
      if (!map.getPane("regionPane")) {
        map.createPane("regionPane");
        map.getPane("regionPane").style.zIndex = 400; // below default
      }
      if (!map.getPane("woredaPane")) {
        map.createPane("woredaPane");
        map.getPane("woredaPane").style.zIndex = 450; // above region
      }
    }, [map]);

    return (
      <>
        {regions && <RegionLayer data={regions} />}
        {geojsonData && (
          <WoredaLayer
            data={geojsonData}
            selectedFactor={selectedFactor}
            selectedCategories={selectedCategories}
          />
        )}
        <Legend
          selectedFactor={selectedFactor}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
        />
      </>
    );
  };

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      {/* Factor selector */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          display: "flex",
        }}
      >
        {["temperature", "humidity", "rainfall"].map((factor) => (
          <button
            key={factor}
            onClick={() => setSelectedFactor(factor)}
            style={{
              flex: 1,
              padding: "6px 12px",
              background: selectedFactor === factor ? "#024795" : "transparent",
              color: selectedFactor === factor ? "#fff" : "#333",
              border: "none",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {factor.charAt(0).toUpperCase() + factor.slice(1)}
          </button>
        ))}
      </div>

      <MapContainer
        center={[9.145, 40.4897]}
        zoom={6}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
      >
        <MapLayers />
      </MapContainer>
    </div>
  );
}
